/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  IonPage,
  IonContent,
} from "@ionic/react";
import React, { useState, useRef, ChangeEvent, FormEvent } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Grid,
  Divider,
  useMediaQuery,
  useTheme as useMuiTheme,
  Avatar,
  styled,
  Link as MuiLink,
  createTheme,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  SelectChangeEvent,
} from "@mui/material";
import {
  ArrowBack,
  School,
  VerifiedUser,
  ChevronRight,
  Logout,
  CloudUpload,
  Delete,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { ThemeProvider } from "@emotion/react";
import { getUser, baseUrl, requestID, config } from "../../common/utils";
import axios from "axios";

// ============================================
// Modern Dashboard Theme (MUI5) - Matching DeltaGovPortal
// ============================================
const theme = createTheme({
  palette: {
    primary: {
      main: "#243b5a", // deep navy from spec
    },
    secondary: {
      main: "#ef4444", // red for logout button
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "2rem",
      fontWeight: 700,
      color: "#1f2937",
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.875rem",
      color: "#6b7280",
    },
    body2: {
      fontSize: "0.875rem",
      color: "#374151",
    },
    caption: {
      fontSize: "0.75rem",
      letterSpacing: "1px",
      color: "#6b7280",
      textTransform: "uppercase",
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "#ffffff",
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          borderRadius: "6px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            borderColor: "#243b5a",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          padding: "8px 16px",
          borderRadius: 6,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#d1d5db",
          borderWidth: "1px",
        },
      },
    },
  },
});

// ============================================
// Styled Components - Matching DeltaGovPortal
// ============================================
const Header = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  padding: "16px 40px",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
}));

const HeaderTopRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const HeaderBottomRow = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
  marginTop: "8px",
});

const LogoCircle = styled(Box)({
  width: "55px",
  height: "55px",
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  color: "#243b5a",
  fontWeight: "bold",
  border: "2px solid #f0c45a",
  boxSizing: "border-box",
  marginRight: "16px",
});

const FooterLink = styled(MuiLink)({
  color: "#d1d5db",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "#ffffff",
  },
});

const SchoolInfoBox = styled(Box)({
  textAlign: "right",
  color: "#f1f5f9",
});

const LogoutButton = styled(Button)({
  backgroundColor: "#ef4444",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#dc2626",
  },
  "& svg": {
    marginRight: "8px",
  },
});

// Types
interface LocationState {
  schoolCode?: string;
  schoolName?: string;
  lgaCode?: string;
}

interface FormData {
  schoolId: string;
  firstname: string;
  othername: string;
  lastname: string;
  gender: string;
  schoolType: string;
  dateOfBirth: string;
  passport: File | null;
  englishTerm1: string;
  englishTerm2: string;
  englishTerm3: string;
  arithmeticTerm1: string;
  arithmeticTerm2: string;
  arithmeticTerm3: string;
  generalTerm1: string;
  generalTerm2: string;
  generalTerm3: string;
  religiousType: string;
  religiousTerm1: string;
  religiousTerm2: string;
  religiousTerm3: string;
  year: string;
}

// Helper Component for Inline Form Fields
interface FormFieldProps {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  md?: number;
}

const FormField = ({ label, children, required, md = 6 }: FormFieldProps) => (
  <Grid item xs={12} md={md}>
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "center" },
        gap: { xs: 1, sm: 2 },
        mb: 1,
      }}
    >
      <Typography
        sx={{
          minWidth: { sm: 140 },
          fontWeight: 600,
          fontSize: "14px",
          color: "#4b5563",
        }}
      >
        {label}
        {required && (
          <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
        )}
      </Typography>
      <Box sx={{ flex: 1, width: "100%" }}>{children}</Box>
    </Box>
  </Grid>
);

// Score Section Component
interface ScoreSectionProps {
  label: string;
  prefix: string;
  formData: FormData;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

const ScoreSection = ({ label, prefix, formData, handleChange }: ScoreSectionProps) => {
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#fcfcfc",
      transition: "all 0.2s ease-in-out",
      "& fieldset": { borderColor: "#e5e7eb" },
      "&:hover fieldset": { borderColor: "#d1d5db" },
      "&.Mui-focused fieldset": { borderColor: "#1d3557", borderWidth: "2px" },
    },
    "& .MuiInputBase-input": { fontSize: "14px", py: 1.5 },
  };

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: "16px",
        backgroundColor: "#f8fafc",
        border: "1px solid #e5e7eb",
        height: "100%",
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "14px",
          color: "#1d3557",
          mb: 2.5,
          textTransform: "uppercase",
          letterSpacing: "0.025em",
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 2 }}>
        {[1, 2, 3].map((term) => (
          <TextField
            key={term}
            label={`Term ${term}`}
            name={`${prefix}Term${term}`}
            type="number"
            value={formData[`${prefix}Term${term}` as keyof FormData] as string}
            onChange={handleChange}
            fullWidth
            size="small"
            inputProps={{ min: 0, max: 100 }}
            sx={inputSx}
          />
        ))}
      </Box>
    </Box>
  );
};

// ============================================
// Main SchoolRegistration Component
// ============================================
const SchoolRegistration: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const state = location.state as LocationState || {};

  // Get user data from utils
  const user = getUser();
  
  // Get school and LGA info from state or user data
  const schoolCode = state.schoolCode || user?.schoolCode || "";
  const schoolName = state.schoolName || user?.schoolName || "";
  const lgaCode = state.lgaCode || user?.lgaCode || "";

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [passportPreview, setPassportPreview] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const initialState: FormData = {
    schoolId: schoolCode,
    firstname: "",
    othername: "",
    lastname: "",
    gender: "",
    schoolType: "",
    dateOfBirth: "",
    passport: null,
    englishTerm1: "",
    englishTerm2: "",
    englishTerm3: "",
    arithmeticTerm1: "",
    arithmeticTerm2: "",
    arithmeticTerm3: "",
    generalTerm1: "",
    generalTerm2: "",
    generalTerm3: "",
    religiousType: "",
    religiousTerm1: "",
    religiousTerm2: "",
    religiousTerm3: "",
    year: "2026/2027",
  };

  const [formData, setFormData] = useState<FormData>({ ...initialState });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      setFormData({ ...formData, passport: file });
      const reader = new FileReader();
      reader.onloadend = () => setPassportPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const removePassport = () => {
    setFormData({ ...formData, passport: null });
    setPassportPreview(null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const fd = new FormData();

      fd.append("lgaCode", lgaCode);
      fd.append("schoolcode", schoolCode);
      fd.append("requestID", requestID());

      Object.keys(formData).forEach((key) => {
        const value = formData[key as keyof FormData];

        if (value !== null && value !== undefined && value !== "") {
          if (key === 'passport' && value instanceof File) {
            fd.append(key, value);
          } else if (typeof value === 'string') {
            fd.append(key, value);
          }
        }
      });

      const res = await axios.post(
        `${baseUrl()}schools/registerStudent`,
        fd,
        config
      );
      
      console.log(res.data);
      return;
      if (res.data.status) {
        setMessage("Student Registered Successfully");
        setFormData(initialState);
        setPassportPreview(null);
        if (formRef.current) {
          formRef.current.reset();
        }

        // Optional short delay so user sees success
        setTimeout(() => {
          history.push("/registered-students", {
            state: {
              lgaCode,
              schoolCode,
            },
          });
        }, 3000);
      } else {
        setMessage(res.data.message || "Registration failed");
      }

    } catch (err) {
      console.error(err);
      setMessage("Server error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    // Clear session data here
    history.push("/login");
  };

  // Handle return to dashboard
  const handleBackToDashboard = () => {
    history.push("/registered-students");
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#fcfcfc",
      transition: "all 0.2s ease-in-out",
      "& fieldset": { borderColor: "#e5e7eb" },
      "&:hover fieldset": { borderColor: "#d1d5db" },
      "&.Mui-focused fieldset": { borderColor: "#1d3557", borderWidth: "2px" },
    },
    "& .MuiInputBase-input": { fontSize: "14px", py: 1.5 },
  };

  const selectSx = {
    borderRadius: "10px",
    fontSize: "14px",
    backgroundColor: "#fcfcfc",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1d3557",
      borderWidth: "2px",
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <IonPage>
        <IonContent>
          {/* Full page layout */}
          <Box sx={{
            bgcolor: "background.default",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}>
            {/* HEADER - Matching SchoolDashboard */}
            <Header>
              <HeaderTopRow>
                {/* Left side - Logo and Ministry info */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LogoCircle>⚖️</LogoCircle>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#cbd5e1", letterSpacing: "0.5px", display: "block" }}>
                      DELTA STATE GOVERNMENT
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600, lineHeight: 1.2 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Student Registration
                    </Typography>
                  </Box>
                </Box>

                {/* Right side - School info */}
                <SchoolInfoBox>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "#ffffff", textTransform: "uppercase" }}
                  >
                    {schoolName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                    LGA: {lgaCode} | Code: {schoolCode}
                  </Typography>
                </SchoolInfoBox>
              </HeaderTopRow>

              {/* Bottom row - Logout button */}
              <HeaderBottomRow>
                <LogoutButton
                  variant="contained"
                  onClick={handleLogout}
                  startIcon={<Logout />}
                >
                  Logout
                </LogoutButton>
              </HeaderBottomRow>
            </Header>

            {/* MAIN CONTENT */}
            <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
              {/* Back to Dashboard Link */}
              <Button
                onClick={handleBackToDashboard}
                startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                sx={{
                  color: "#243b5a",
                  fontSize: "13px",
                  textTransform: "none",
                  p: 0,
                  mb: 3,
                  '&:hover': {
                    textDecoration: "underline",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Back to Dashboard
              </Button>

              {/* Header Section */}
              <Box sx={{ mb: 4, textAlign: "center" }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 850,
                    color: "#111827",
                    mb: 1,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Student Registration
                </Typography>
                <Typography
                  sx={{
                    fontSize: "16px",
                    color: "#6b7280",
                    maxWidth: 600,
                    mx: "auto",
                  }}
                >
                  Fill in the details below to register a new student for{" "}
                  <span style={{ color: "#1d3557", fontWeight: 700 }}>
                    {schoolName || "Your School"}
                  </span>
                </Typography>
              </Box>

              {message && (
                <Alert
                  severity={message.includes("Successfully") ? "success" : "error"}
                  sx={{
                    mb: 4,
                    whiteSpace: "pre-line",
                    borderRadius: "12px",
                    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    border: "1px solid",
                    borderColor: message.includes("Successfully")
                      ? "#bbf7d0"
                      : "#fecaca",
                  }}
                >
                  {message}
                </Alert>
              )}

              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 6 },
                  borderRadius: "24px",
                  border: "1px solid #e5e7eb",
                  backgroundColor: "#ffffff",
                  boxShadow:
                    "0 10px 15px -3px rgb(0 0 0 / 0.05), 0 4px 6px -4px rgb(0 0 0 / 0.05)",
                }}
              >
                <form ref={formRef} onSubmit={handleSubmit}>
                  {/* Personal Information Section */}
                  <Box sx={{ mb: 5 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 3, gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 4,
                          height: 24,
                          backgroundColor: "#1d3557",
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        sx={{ fontWeight: 800, fontSize: "18px", color: "#111827" }}
                      >
                        Personal Information
                      </Typography>
                    </Box>

                    <Grid container spacing={3}>
                      <FormField label="LGA Code" required>
                        <TextField
                          value={lgaCode}
                          InputProps={{ readOnly: true }}
                          fullWidth
                          size="small"
                          sx={inputSx}
                        />
                      </FormField>

                      <FormField label="School ID" required>
                        <TextField
                          name="schoolId"
                          value={formData.schoolId}
                          InputProps={{ readOnly: true }}
                          fullWidth
                          size="small"
                          sx={inputSx}
                        />
                      </FormField>

                      <FormField label="Last Name" required>
                        <TextField
                          name="lastname"
                          placeholder="Enter surname"
                          value={formData.lastname}
                          onChange={handleChange}
                          fullWidth
                          size="small"
                          sx={inputSx}
                        />
                      </FormField>

                      <FormField label="First Name" required>
                        <TextField
                          name="firstname"
                          placeholder="Enter first name"
                          value={formData.firstname}
                          onChange={handleChange}
                          fullWidth
                          size="small"
                          sx={inputSx}
                        />
                      </FormField>

                      <FormField label="Other Name">
                        <TextField
                          name="othername"
                          placeholder="Enter other name (optional)"
                          value={formData.othername}
                          onChange={handleChange}
                          fullWidth
                          size="small"
                          sx={inputSx}
                        />
                      </FormField>

                      <FormField label="School Type" required>
                        <FormControl fullWidth size="small">
                          <Select
                            name="schoolType"
                            value={formData.schoolType}
                            onChange={handleSelectChange}
                            sx={selectSx}
                            displayEmpty
                          >
                            <MenuItem value="" disabled>
                              Select school type
                            </MenuItem>
                            <MenuItem value="Private">Private</MenuItem>
                            <MenuItem value="Public">Public</MenuItem>
                          </Select>
                        </FormControl>
                      </FormField>

                      <FormField label="Gender" required>
                        <FormControl fullWidth size="small">
                          <Select
                            name="gender"
                            value={formData.gender}
                            onChange={handleSelectChange}
                            sx={selectSx}
                            displayEmpty
                          >
                            <MenuItem value="" disabled>
                              Select gender
                            </MenuItem>
                            <MenuItem value="Male">Male</MenuItem>
                            <MenuItem value="Female">Female</MenuItem>
                          </Select>
                        </FormControl>
                      </FormField>

                      <FormField label="Date of Birth" required>
                        <TextField
                          type="date"
                          name="dateOfBirth"
                          value={formData.dateOfBirth}
                          onChange={handleChange}
                          fullWidth
                          size="small"
                          sx={inputSx}
                        />
                      </FormField>

                      {/* Passport Upload */}
                      <FormField label="Passport Photo" md={12}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
                          {!passportPreview ? (
                            <Button
                              variant="outlined"
                              component="label"
                              sx={{
                                borderStyle: "dashed",
                                borderWidth: "2px",
                                borderRadius: "12px",
                                px: 4,
                                py: 1.5,
                                borderColor: "#d1d5db",
                                color: "#4b5563",
                                textTransform: "none",
                                "&:hover": {
                                  borderColor: "#1d3557",
                                  backgroundColor: "#f8fafc",
                                },
                              }}
                            >
                              <CloudUpload sx={{ mr: 1 }} /> Click to Upload
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleFileChange}
                              />
                            </Button>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                p: 1.5,
                                border: "1px solid #e5e7eb",
                                borderRadius: "12px",
                                backgroundColor: "#f8fafc",
                              }}
                            >
                              <Box
                                component="img"
                                src={passportPreview}
                                alt="Passport preview"
                                sx={{
                                  width: 50,
                                  height: 50,
                                  borderRadius: "8px",
                                  objectFit: "cover",
                                  border: "2px solid #fff",
                                }}
                              />
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#374151",
                                  }}
                                >
                                  Image Uploaded
                                </Typography>
                                <Button
                                  size="small"
                                  onClick={removePassport}
                                  startIcon={<Delete />}
                                  sx={{
                                    p: 0,
                                    minWidth: "auto",
                                    fontSize: "12px",
                                    color: "#ef4444",
                                    textTransform: "none",
                                    "&:hover": {
                                      backgroundColor: "transparent",
                                      textDecoration: "underline",
                                    },
                                  }}
                                >
                                  Remove
                                </Button>
                              </Box>
                            </Box>
                          )}
                          <Typography
                            sx={{ fontSize: "12px", color: "#9ca3af", maxWidth: 200 }}
                          >
                            JPG, PNG or GIF. Max size 2MB.
                          </Typography>
                        </Box>
                      </FormField>
                    </Grid>
                  </Box>

                  <Divider sx={{ my: 6, borderColor: "#f3f4f6" }} />

                  {/* Academic Performance Section */}
                  <Box sx={{ mb: 5 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 4, gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 4,
                          height: 24,
                          backgroundColor: "#1d3557",
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        sx={{ fontWeight: 800, fontSize: "18px", color: "#111827" }}
                      >
                        Academic Performance (Continuous Assessment)
                      </Typography>
                    </Box>

                    <Grid container spacing={4}>
                      <Grid item xs={12}>
                        <ScoreSection
                          label="English Studies"
                          prefix="english"
                          formData={formData}
                          handleChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ScoreSection
                          label="Arithmetic"
                          prefix="arithmetic"
                          formData={formData}
                          handleChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ScoreSection
                          label="General Paper"
                          prefix="general"
                          formData={formData}
                          handleChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: "16px",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e5e7eb",
                            height: "100%",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "14px",
                              color: "#1d3557",
                              mb: 2.5,
                              textTransform: "uppercase",
                              letterSpacing: "0.025em",
                            }}
                          >
                            Religious Education
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <FormControl fullWidth size="small">
                                <Select
                                  name="religiousType"
                                  value={formData.religiousType}
                                  onChange={handleSelectChange}
                                  sx={selectSx}
                                  displayEmpty
                                >
                                  <MenuItem value="" disabled>
                                    Select Subject Type
                                  </MenuItem>
                                  <MenuItem value="Islam">
                                    Islamic Religious Studies
                                  </MenuItem>
                                  <MenuItem value="Christian">
                                    Christian Religious Studies
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                                {[
                                  "religiousTerm1",
                                  "religiousTerm2",
                                  "religiousTerm3",
                                ].map((field, i) => (
                                  <TextField
                                    key={field}
                                    label={`Term ${i + 1}`}
                                    name={field}
                                    type="number"
                                    value={formData[field as keyof FormData] as string}
                                    onChange={handleChange}
                                    fullWidth
                                    size="small"
                                    inputProps={{ min: 0, max: 100 }}
                                    sx={inputSx}
                                  />
                                ))}
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box sx={{ mt: 8 }}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                      sx={{
                        height: 60,
                        borderRadius: "14px",
                        fontWeight: 800,
                        fontSize: 18,
                        backgroundColor: "#1d3557",
                        textTransform: "none",
                        boxShadow: "0 10px 15px -3px rgba(29, 53, 87, 0.3)",
                        "&:hover": {
                          backgroundColor: "#15294a",
                          boxShadow: "0 20px 25px -5px rgba(29, 53, 87, 0.4)",
                        },
                        "&:disabled": { backgroundColor: "#9ca3af" },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={28} sx={{ color: "#fff" }} />
                      ) : (
                        "Submit Registration"
                      )}
                    </Button>
                    <Typography
                      sx={{
                        textAlign: "center",
                        mt: 2,
                        fontSize: "14px",
                        color: "#6b7280",
                      }}
                    >
                      Ensure all details are accurate before submitting. This action
                      cannot be easily undone.
                    </Typography>
                  </Box>
                </form>
              </Paper>
            </Container>

            {/* FOOTER - Matching DeltaGovPortal exactly */}
            <Box component="footer" sx={{ bgcolor: "primary.main", color: "#d1d5db", py: { xs: 5, md: 6 }, mt: "auto" }}>
              <Container maxWidth="lg">
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      MINISTRY OF PRIMARY EDUCATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Delta State Government
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Asaba, Delta State, Nigeria
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      CONTACT INFORMATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Email: info@dsgmope.ng
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Address: Ministry Secretariat, Asaba
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Telephone: +234 (0)901 234 5678
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      OFFICIAL RESOURCES
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <FooterLink href="#">www.dsgmope.ng</FooterLink>
                    </Typography>
                    <Typography variant="body2">
                      <FooterLink href="#">Delta State Government Portal</FooterLink>
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 3 }} />

                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", maxWidth: 900, mx: "auto", mb: 2 }}
                >
                  OFFICIAL NOTICE: This is an official portal of the Delta State Government, Ministry of Primary Education. Unauthorized access, misuse, or tampering with this system is prohibited and may be subject to legal action. All information provided is for official purposes only.
                </Typography>

                <Typography variant="body2" align="center" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}>
                  © 2026 Delta State Government, Ministry of Primary Education. All rights reserved.
                </Typography>
              </Container>
            </Box>
          </Box>
        </IonContent>
      </IonPage>
    </ThemeProvider>
  );
};

export default SchoolRegistration;